import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Smartphone, Save, Loader2, Check, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientWorkspacesPanel({ clientId, getAdminAxios, permKeys }) {
  const adminAxios = getAdminAxios();
  const [maxWS, setMaxWS] = useState(1);
  const [draftMax, setDraftMax] = useState(1);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMax, setSavingMax] = useState(false);
  const [savedMax, setSavedMax] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [savingPerm, setSavingPerm] = useState({});
  const [savedPerm, setSavedPerm] = useState({});
  const [error, setError] = useState("");

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await adminAxios.get(`${API}/admin/clients/${clientId}/workspaces`);
      setMaxWS(data.max_workspaces || 1);
      setDraftMax(data.max_workspaces || 1);
      setWorkspaces(data.workspaces || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const saveMax = async () => {
    setSavingMax(true); setSavedMax(false); setError("");
    try {
      await adminAxios.put(`${API}/admin/clients/${clientId}/max-workspaces`, { max_workspaces: Number(draftMax) || 1 });
      setMaxWS(Number(draftMax) || 1);
      setSavedMax(true);
      setTimeout(() => setSavedMax(false), 2500);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSavingMax(false); }
  };

  const togglePerm = (wid, key, value) => {
    setWorkspaces((ws) => ws.map((w) =>
      w.id === wid ? { ...w, permissions: { ...w.permissions, [key]: value } } : w
    ));
  };

  const savePerms = async (wid) => {
    setSavingPerm((p) => ({ ...p, [wid]: true }));
    setSavedPerm((p) => ({ ...p, [wid]: false }));
    setError("");
    try {
      const ws = workspaces.find((w) => w.id === wid);
      await adminAxios.put(`${API}/admin/workspaces/${wid}/permissions`, { perms: ws.permissions || {} });
      setSavedPerm((p) => ({ ...p, [wid]: true }));
      setTimeout(() => setSavedPerm((p) => ({ ...p, [wid]: false })), 2500);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSavingPerm((p) => ({ ...p, [wid]: false })); }
  };

  if (loading) {
    return <Card className="bg-[#13112b] border-indigo-900/30 p-6"><Loader2 className="w-5 h-5 text-amber-400 animate-spin" /></Card>;
  }

  return (
    <div className="space-y-4">
      {/* Max workspaces config */}
      <Card className="bg-[#13112b] border-indigo-900/30">
        <CardHeader>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-amber-400" /> Cantidad máxima de WhatsApps permitidos
          </CardTitle>
          <CardDescription className="text-slate-400">
            El cliente puede crear hasta este número de instancias de WhatsApp en su dashboard.
            Subilo cuando contrate más, bajalo si quiere reducir.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Label className="text-slate-300">Máximo</Label>
          <Input
            type="number" min={1} max={50}
            value={draftMax}
            onChange={(e) => setDraftMax(e.target.value)}
            className="bg-white/5 border-white/10 text-white w-24"
            data-testid="admin-max-workspaces-input"
          />
          <Button onClick={saveMax} disabled={savingMax || Number(draftMax) === maxWS} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="admin-save-max-workspaces">
            {savingMax ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
          </Button>
          {savedMax && <span className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Guardado</span>}
          <span className="text-xs text-slate-500 ml-auto">Usa actualmente {workspaces.length}/{maxWS}</span>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-700/40 bg-red-900/20">
          <CardContent className="p-3 flex items-center gap-2 text-sm text-red-300">
            <AlertTriangle className="w-4 h-4" /> {error}
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card className="bg-[#13112b] border-indigo-900/30">
        <CardHeader>
          <CardTitle className="text-base text-white">WhatsApps del cliente ({workspaces.length})</CardTitle>
          <CardDescription className="text-slate-400">
            Cada WhatsApp tiene su propio set de permisos. Tocá en uno para expandir y editarlos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {workspaces.length === 0 ? (
            <p className="text-sm text-slate-500">No hay workspaces todavía.</p>
          ) : workspaces.map((w) => {
            const isOpen = expanded === w.id;
            return (
              <div key={w.id} className="border border-white/10 rounded-lg overflow-hidden" data-testid={`admin-ws-${w.id}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : w.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition text-left"
                  data-testid={`admin-ws-toggle-${w.id}`}
                >
                  <Smartphone className="w-4 h-4 text-emerald-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{w.name}</p>
                    <p className="text-xs text-slate-500">
                      {w.is_active ? "Activo" : "Pausado"} · creado {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                    </p>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
                {isOpen && (
                  <div className="px-4 py-3 border-t border-white/10 bg-black/20 space-y-2">
                    {permKeys.map((p) => (
                      <div key={p.key} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                        <Label className="text-slate-300 text-sm">{p.label}</Label>
                        <Switch
                          checked={(w.permissions || {})[p.key] === true}
                          onCheckedChange={(v) => togglePerm(w.id, p.key, v)}
                          className="data-[state=checked]:bg-amber-500"
                          data-testid={`admin-ws-${w.id}-perm-${p.key}`}
                        />
                      </div>
                    ))}
                    <div className="pt-2 flex items-center gap-2">
                      <Button
                        onClick={() => savePerms(w.id)}
                        disabled={savingPerm[w.id]}
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                        data-testid={`admin-ws-${w.id}-save`}
                      >
                        {savingPerm[w.id] ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        Guardar permisos
                      </Button>
                      {savedPerm[w.id] && <span className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Guardado</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
